import CheckCircleOutlineOutlinedIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import DownloadIcon from '@mui/icons-material/Download';
import LockOutlined from '@mui/icons-material/LockOutlined';
import ReplayIcon from '@mui/icons-material/Replay';
import { Button, ButtonGroup, CircularProgress } from '@mui/material';
import { EnhancedDialog } from 'components/enhanced/dialog/EnhancedDialog';
import { EnhancedTabs, TabItem } from 'components/enhanced/tabs/EnhancedTabs';
import { IsBottomFromTechnicianCoordinator } from 'shared/permissions/IsBottomFromTechnicianCoordinator';
import { IsSuperAdminGuard } from 'shared/permissions/IsSuperAdmin';
import { useIsTenantAdministrator } from 'shared/permissions/IsTenantAdministrator';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import TenantPage from 'layouts/TenantPage';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import toast from 'shared/utils/toast';
import ShowEmailTiketingDialog from '../components/dialog/ShowEmailTiketingDialog';
import TicketForm from '../components/form/TicketForm';
import { ITicket } from '../types';
import CloseTicketForm, {
  CloseTicketFormValues,
  resolveCloseTicketStatusValue
} from '../components/form/CloseTicketForm';
import { ticketingService } from '../TicketingService';
dayjs.extend(customParseFormat);

export const Ticket = () => {
  const translationPrefix = 'pages.tickets';
  const { t } = useTranslation();

  const { ticketId } = useParams();
  const [ticket, setTicket] = useState<ITicket>();

  const [openDialog, setOpenDialog] = useState(false);
  const [closingTicket, setClosingTicket] = useState(false);
  const [isGenerating, setIsGenerating] = useState<boolean>(false);

  // --------------------------------------------------------- get
  useEffect(() => {
    ticketingService.get(+ticketId).then(setTicket);
  }, []);

  // --------------------------------------------------------- approve ticket
  const approveTicket = () => {
    ticketingService.approvedByUser(+ticketId).then(() => {
      toast.success(t(`${translationPrefix}.ticket-approved`));
      ticketingService.get(+ticketId).then(setTicket);
    });
  };

  // --------------------------------------------------------- update ticket
  const updateTicket = (values) => {
    // bisogna escludere contract_equipment altrimenti da errore (contract_equipment è l'id in UPDATE ma qui si mostra il equipment_code)
    delete values['contract_equipment'];
    // if (values.created_at) {
    //   const parsed = dayjs(values.created_at, 'DD/MM/YYYY HH:mm');
    //   values.created_at = parsed;
    // }
    ticketingService.update(+ticketId, values).then((res) => {
      toast.success(t(`${translationPrefix}.ticket-updated`));
      setTicket(res);
    });
  };

  // --------------------------------------------------------- dialog close ticket
  const handleOpenDialog = () => {
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const closeTicket = async (values: CloseTicketFormValues) => {
    const statusValue = resolveCloseTicketStatusValue(values?.status);
    const needsSpecialistArrival = statusValue === 5 || statusValue === 9;
    if (needsSpecialistArrival && !ticket?.specialist_arrival_at) {
      toast.error('Inserire e salvare “Arrivo tecnico specialista” prima di chiudere il ticket.');
      return;
    }
    if (statusValue === 5 && !ticket?.technical_report && !values.technical_report_file) {
      toast.error(t(`${translationPrefix}.close-modal.report-missing-error`));
      return;
    }

    setClosingTicket(true);
    try {
      if (statusValue === 5 && !ticket?.technical_report && values.technical_report_file) {
        await ticketingService.attachFiles(
          values.technical_report_file,
          'technical_report',
          +ticketId
        );
      }
      const closePayload = { ...values };
      delete closePayload.technical_report_file;
      await ticketingService.closeTicket(+ticketId, closePayload);
      toast.success(t(`${translationPrefix}.ticket-closed`));
      const updated = await ticketingService.get(+ticketId);
      setTicket(updated);
      setOpenDialog(false);
    } catch (e: unknown) {
      const detail =
        e &&
        typeof e === 'object' &&
        'response' in e &&
        (e as { response?: { data?: { detail?: string } } }).response?.data?.detail;
      toast.error(detail ?? 'Errore durante la chiusura del ticket');
    } finally {
      setClosingTicket(false);
    }
  };

  const reopenTicket = () => {
    ticketingService.reopenTicket(+ticketId).then(() => {
      toast.success(t(`${translationPrefix}.ticket-reopened`));
      ticketingService.get(+ticketId).then(setTicket);
    });
  };

  // --------------------------------------------------------- send ticket
  const sendTicket = (values) => {
    ticketingService.sendTicket(+ticketId, values).then(() => {
      toast.success(t(`${translationPrefix}.sent`));
      ticketingService.get(+ticketId).then(setTicket);
    });
  };

  // --------------------------------------------------------- generate PDF

  const handlegenerateEmailPDF = async () => {
    if (!ticket?.approvation_status) return;
    setIsGenerating(true);

    try {
      await ticketingService.generateEmailPdf(+ticketId);
      toast.success(t(`${translationPrefix}.email-pdf-generated`));

      await ticketingService.downloadEmailPdf(+ticketId);
      toast.success(t(`actions.successfully.downloaded`));
    } catch (error) {
      toast.apiError(error);
    } finally {
      setIsGenerating(false);
    }
  };

  const canViewToApprove = IsSuperAdminGuard() || useIsTenantAdministrator();

  const tabConfig: TabItem[] = useMemo(() => {
    return [
      {
        label: t(`global.base-info`),
        component: <TicketForm ticket={ticket} onSubmit={updateTicket} />
      }
    ];
  }, [ticket, ticketId]);

  return (
    <TenantPage
      title={t(`Ticket #${ticketId}`)}
      subtitle={t(`${translationPrefix}.edit-subtitle`)}
      entityCode={`#${ticketId}`}
      menuRight={
        <div className="flex-row justify-end">
          <IsBottomFromTechnicianCoordinator>
            {ticket && (
              <ButtonGroup size="small">
                {/* Sezione Approvazione */}
                {!ticket.approvation_status && canViewToApprove && (
                  <Button variant="outlined" size="small" color="info" onClick={approveTicket}>
                    <CheckCircleOutlineOutlinedIcon className="mr-2" />
                    {t(`${translationPrefix}.approve`)}
                  </Button>
                )}

                {/* Sezione Ticket Approvato */}
                {ticket.approvation_status && (
                  <div className="flex flex-wrap items-center gap-4">
                    {/* Gruppo PDF */}
                    <Button
                      disabled={isGenerating}
                      variant="outlined"
                      onClick={() => {
                        handlegenerateEmailPDF();
                      }}>
                      {isGenerating ? (
                        <CircularProgress size={16} color="inherit" />
                      ) : (
                        <DownloadIcon />
                      )}

                      {isGenerating
                        ? t('Generazione in corso..')
                        : t(`${translationPrefix}.email-pdf`)}
                    </Button>

                    {/* Bottone Riapertura */}
                    {ticket.can_reopen && (
                      <Button
                        variant="outlined"
                        size="small"
                        color="warning"
                        onClick={reopenTicket}
                        disabled={!ticket.can_reopen}>
                        <ReplayIcon className="mr-2" />
                        {t(`${translationPrefix}.reopen`)}
                      </Button>
                    )}

                    {/* Bottone Chiusura */}
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={handleOpenDialog}
                      disabled={
                        !(
                          [
                            'OPEN',
                            'INTERVENTION_REQUESTED',
                            'INTERVENTION_PENDING',
                            'INTERVENTION_COMPLETED_MISSING_REPORT',
                            'UNDETERMINED'
                          ].indexOf(ticket.status.display_name) > -1
                        )
                      }>
                      <LockOutlined className="mr-2" />
                      {t(`${translationPrefix}.close`)}
                    </Button>

                    {/* Dialog Invio Email */}
                    <ShowEmailTiketingDialog
                      hasBeenSent={ticket.sent}
                      equipment_id={ticket.equipment_id}
                      equipment_cc_email={ticket.equipment_cc_ticketing_email}
                      equipment_email={ticket.equipment_email}
                      onSubmit={sendTicket}
                    />
                  </div>
                )}
              </ButtonGroup>
            )}
          </IsBottomFromTechnicianCoordinator>
        </div>
      }>
      <EnhancedTabs
        tabs={tabConfig}
        variant="scrollable"
        documentConfig={{
          serviceName: 'ticket',
          entityId: ticket?.id,
          dictParams: { ticket: ticketId },
          customFolderName: 'menu.tickets/' + ticket?.equipment + '_' + ticketId,
          folderDescription: `Strumento: ${ticket?.equipment}; Data chiusura: ${
            ticket?.resolved_at ?? 'N.D.'
          }; Conforme: ${ticket?.compliant ? `SI` : `NO`}; `
        }}
        historyConfig={{ service: ticketingService, entityId: ticket?.id }}
      />

      {/* Dialog close ticketing */}
      <EnhancedDialog
        open={openDialog}
        onClose={handleCloseDialog}
        title={t(`${translationPrefix}.close`)}
        submitLabel={t('actions.save', 'Salva')}
        cancelLabel={t('actions.reset', 'Annulla')}
        maxWidth="lg"
        submitButtonProps={{
          type: 'submit',
          form: 'closeTicket'
        }}>
        <CloseTicketForm
          key={openDialog ? `close-ticket-${ticketId}` : 'close-ticket-closed'}
          ticketId={+ticketId}
          technicalReportFilename={
            typeof ticket?.technical_report === 'string' ? ticket.technical_report : null
          }
          onSubmit={closeTicket}
        />
      </EnhancedDialog>
    </TenantPage>
  );
};

export default Ticket;
